import React, { createContext, useContext, useState } from 'react';

/**
 * Global "watch vs all users" segment (feature 023).
 *
 * The segment defaults to 'watch' so every admin list/summary page loads watch
 * users only. A single global toggle (see components/common/UserSegmentToggle)
 * switches it to 'without_watch' (non-watch users) or 'all'. The provider wraps
 * <Routes> so the choice is shared across every page without resetting on navigation.
 *
 * Persistence to sessionStorage (survive refresh, reset on new session) is added
 * in a later slice (US4); this baseline holds the segment in memory.
 *
 * @typedef {'watch' | 'without_watch' | 'all'} Segment
 */

const DEFAULT_SEGMENT = 'watch';

export const UserSegmentContext = createContext({
  segment: DEFAULT_SEGMENT,
  setSegment: () => {},
});

export function UserSegmentProvider({ children }) {
  const [segment, setSegment] = useState(DEFAULT_SEGMENT);
  return (
    <UserSegmentContext.Provider value={{ segment, setSegment }}>
      {children}
    </UserSegmentContext.Provider>
  );
}

export function useUserSegment() {
  return useContext(UserSegmentContext);
}

/**
 * The `watch_filter` query value for a segment (single source of truth).
 * 'watch' -> 'with_watch'; 'without_watch' -> 'without_watch'; 'all' -> '' (no filter).
 * @param {Segment} segment
 * @returns {string}
 */
export function watchFilterValue(segment) {
  if (segment === 'watch') return 'with_watch';
  if (segment === 'without_watch') return 'without_watch';
  return '';
}

/**
 * Query-string fragment to append to an admin list endpoint URL for the segment.
 * Returns e.g. '&watch_filter=with_watch', or '' for the whole population.
 * @param {Segment} segment
 * @returns {string}
 */
export function watchParam(segment) {
  const v = watchFilterValue(segment);
  return v ? `&watch_filter=${v}` : '';
}

export default UserSegmentContext;
